import React from 'react';
import { makeStyles } from '@material-ui/styles';
import PropTypes from 'prop-types';
import { Card, CardBody } from 'reactstrap';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import Tooltip from 'components/tooltips/Tooltip';
import { MIN_DESKTOP_WIDTH } from 'constants/misc';
import { SHOW_ANALYTICS_TOOLTIP } from 'constants/analytics';
import LineChart from 'components/Charts/LineChart';
import { analyticsTrack } from '../../utils/analytics';

const useStyles = makeStyles(theme => ({
  cardBody: props => ({
    display: 'flex',
    alignItems: props.graphData ? 'flex-end' : 'center',
    justifyContent: props.graphData ? 'start' : 'center',
    flexDirection: props.graphData ? 'row' : 'column',
    padding: '0.75rem !important',
  }),
  dataWrapper: props => ({
    width: props.graphData ? '60%' : '100%',
  }),
  value: props => ({
    color: theme.palette.secondary.text,
    fontSize: '1.5rem',
    fontWeight: 600,
    justifyContent: props.graphData ? 'left' : 'center',
    display: 'flex',
    [`@media screen and (max-width: ${MIN_DESKTOP_WIDTH}px)`]: {
      fontSize: props.graphData ? '1.5rem' : '2rem',
    },
  }),
  label: props => ({
    color: theme.palette.gray.contrastText,
    fontSize: '0.875rem',
    textAlign: props.graphData ? 'left' : 'center',
    fontWeight: 600,
    marginTop: '0.25rem',
    alignItems: 'center',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    wordBreak: 'normal',
    [`@media screen and (max-width: ${MIN_DESKTOP_WIDTH}px)`]: {
      fontSize: props.graphData ? '0.875rem' : '1rem',
      marginTop: '0rem',
    },
  }),
  superScript: {
    display: 'inline-block',
    fontSize: '0.875rem',
    color: theme.palette.secondary['900'],
    marginTop: '0.313rem',
    marginLeft: '0.375rem',
  },
  responsiveWrapper: {
    width: '30%',
    height: '2.813rem',
    marginRight: '0.875rem',
  },
  infoIcon: {
    width: '0.75rem',
    height: '0.75rem',
    color: theme.palette.secondary['300'],
    marginTop: '-0.125rem',
    marginLeft: '0.375rem',
    cursor: 'pointer',
  },
}));

const onShowTooltip = (label) => {
  analyticsTrack(SHOW_ANALYTICS_TOOLTIP, label, true);
};

const StatCard = (props) => {
  const {
    className, value, superScript, tooltip,
    label, graphData, info, color, dataKey,
  } = props;
  const classes = useStyles(props);

  return (
    <Card className={className}>
      <CardBody className={classes.cardBody}>
        {graphData &&
          <div className={classes.responsiveWrapper}>
            <LineChart graphData={graphData} dataKey={dataKey} color={color} />
          </div>
        }
        <div className={classes.dataWrapper}>
          <div className={classes.value}>
            {tooltip ? (
              <Tooltip title={tooltip}>
                <span>{value}</span>
              </Tooltip>
            ) : (
              <span>{value}</span>
            )}
            {superScript !== null &&
              <div className={classes.superScript}>({superScript})</div>
            }
          </div>
          <div className={classes.label}>
            {label}
            {info &&
              <Tooltip title={info} onMouseEnter={() => onShowTooltip(label)}>
                <InfoOutlinedIcon className={classes.infoIcon} />
              </Tooltip>
            }
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

StatCard.propTypes = {
  className: PropTypes.string,
  value: PropTypes.string.isRequired,
  superScript: PropTypes.string,
  info: PropTypes.string,
  label: PropTypes.string.isRequired,
  color: PropTypes.string,
  graphData: PropTypes.shape({}),
  dataKey: PropTypes.string,
  tooltip: PropTypes.string,
};

StatCard.defaultProps = {
  className: '',
  info: null,
  superScript: null,
  graphData: null,
  color: 'blue',
  tooltip: null,
  dataKey: null,
};

export default StatCard;
